/**
 * Machine à états stricte du pipeline de déploiement (cahier des charges §4.2).
 *
 * Source de vérité unique : le builder appelle `assertTransition` avant chaque
 * écriture d'état, l'API l'utilise pour valider les annulations, le front s'en
 * sert pour afficher les segments de la barre de progression.
 */
import { isAwaitingUser, isTerminal, pipelineStepsFor, } from './states.js';
/**
 * Transitions autorisées, par type de job. Chaque état actif peut basculer vers
 * l'étape suivante, vers `FAILED` (avec raison persistée) ou vers `CANCELLED`.
 *
 * Le rollback a sa propre table plutôt qu'une exception glissée dans celle du
 * déploiement : c'est ce qui garantit qu'un rollback ne pourra jamais passer par
 * une étape de construction, même à la suite d'une erreur de programmation.
 */
const TRANSITIONS = {
    DEPLOY: {
        QUEUED: ['TRANSFORMING', 'FAILED', 'CANCELLED'],
        TRANSFORMING: ['AWAITING_REVIEW', 'SCAFFOLDING', 'FAILED', 'CANCELLED'],
        /**
         * Le retour vers `TRANSFORMING` n'est pas un recul : c'est la reprise du job
         * après acceptation. Le pipeline retraverse l'étape en fournissant le code
         * déjà transformé — le même mécanisme que la reprise après échec, qui
         * retraverse elle aussi les étapes déjà franchies au lieu de les rejouer.
         * Modéliser un saut direct vers `SCAFFOLDING` obligerait le pipeline à
         * connaître deux points d'entrée.
         */
        AWAITING_REVIEW: ['TRANSFORMING', 'FAILED', 'CANCELLED'],
        SCAFFOLDING: ['BUILDING', 'FAILED', 'CANCELLED'],
        BUILDING: ['PUSHING', 'FAILED', 'CANCELLED'],
        PUSHING: ['DEPLOYING', 'FAILED', 'CANCELLED'],
        DEPLOYING: ['HEALTHCHECK', 'FAILED', 'CANCELLED'],
        HEALTHCHECK: ['DONE', 'FAILED', 'CANCELLED'],
        DONE: [],
        FAILED: [],
        CANCELLED: [],
    },
    ROLLBACK: {
        // Saut direct à l'étape de déploiement : l'image de la version cible est
        // déjà dans le registre (§5.5).
        QUEUED: ['DEPLOYING', 'FAILED', 'CANCELLED'],
        DEPLOYING: ['HEALTHCHECK', 'FAILED', 'CANCELLED'],
        HEALTHCHECK: ['DONE', 'FAILED', 'CANCELLED'],
        // États inatteignables pour un rollback : il ne transforme rien, il n'y a
        // donc jamais de changement fonctionnel à faire valider.
        TRANSFORMING: [],
        AWAITING_REVIEW: [],
        SCAFFOLDING: [],
        BUILDING: [],
        PUSHING: [],
        DONE: [],
        FAILED: [],
        CANCELLED: [],
    },
};
/** Erreur levée lorsqu'une transition interdite est tentée. */
export class InvalidTransitionError extends Error {
    from;
    to;
    type;
    constructor(from, to, type = 'DEPLOY') {
        super(`Transition interdite pour un job ${type} : ${from} → ${to}`);
        this.name = 'InvalidTransitionError';
        this.from = from;
        this.to = to;
        this.type = type;
    }
}
/** Liste des états atteignables depuis `from`, pour un type de job donné. */
export function allowedTransitions(from, type = 'DEPLOY') {
    return TRANSITIONS[type][from];
}
/** Indique si la transition `from → to` est autorisée pour ce type de job. */
export function canTransition(from, to, type = 'DEPLOY') {
    return TRANSITIONS[type][from].includes(to);
}
/**
 * Vérifie la transition et lève `InvalidTransitionError` si elle est interdite.
 * À appeler systématiquement avant de persister un nouvel état.
 */
export function assertTransition(from, to, type = 'DEPLOY') {
    if (!canTransition(from, to, type)) {
        throw new InvalidTransitionError(from, to, type);
    }
}
/**
 * Étape suivante dans le déroulé nominal (hors échec/annulation).
 * Retourne `null` pour les états terminaux.
 */
export function nextNominalState(from, type = 'DEPLOY') {
    if (isTerminal(from))
        return null;
    const steps = pipelineStepsFor(type);
    if (from === 'QUEUED')
        return steps[0] ?? null;
    const index = steps.indexOf(from);
    if (index === -1)
        return null;
    return index === steps.length - 1 ? 'DONE' : (steps[index + 1] ?? null);
}
/**
 * Droit d'annulation (§4.2) : un utilisateur ne peut annuler que tant que le job
 * est en file d'attente ; un administrateur peut annuler à tout moment.
 *
 * L'attente d'une validation y est assimilée pour l'utilisateur : c'est lui que
 * le pipeline attend, il serait absurde de lui refuser de renoncer. Le refus
 * passe normalement par la route de revue, mais rien ne justifie que le bouton
 * d'annulation, lui, soit grisé.
 */
export function canCancel(role, state) {
    if (isTerminal(state))
        return false;
    return role === 'admin' ? true : state === 'QUEUED' || isAwaitingUser(state);
}
//# sourceMappingURL=transitions.js.map